// Orkestrator E3 §4 — vendor subscription/API policy as data, plus the honest "where it ran / who paid"
// labels. Pure and env-free: the browser (badges, /kebijakan-penyedia) and unit tests load it without
// a database. `policy.server.ts` overlays the `provider_policy` rows (admin overrides) on this seed.
import seed from './provider-policy.json' with { type: 'json' }

export const POLICY_SCENARIOS = ['a', 'b1', 'b2', 'c', 'd'] as const
export type PolicyScenario = (typeof POLICY_SCENARIOS)[number]
export const POLICY_STATUSES = ['allowed', 'gray', 'forbidden'] as const
export type PolicyStatus = (typeof POLICY_STATUSES)[number]
/** Scenarios Bystep actually offers; b1 is never offered, b2 is not offered (see the seed note). */
export const OFFERED_SCENARIOS: readonly PolicyScenario[] = ['a', 'c', 'd']

export interface PolicyCell {
  status: PolicyStatus
  note: string | null
  sourceUrl: string | null
  /** ISO date (YYYY-MM-DD) the cell was last checked */
  verifiedAt: string | null
  /** an admin changed `status`; the seed script never overwrites it */
  overridden?: boolean
}
export interface PolicyProvider { id: string; label: string; scenarios: Partial<Record<PolicyScenario, PolicyCell>> }
export interface PolicyTable {
  version: number
  /** newest verification date of any cell (the page headline) */
  verifiedAt: string | null
  note: string | null
  providers: PolicyProvider[]
}
/** One `provider_policy` row in wire form. */
export interface PolicyRow { provider: string; scenario: string; status: string; note: string | null; sourceUrl: string | null; verifiedAt: string | null; overridden: boolean }

export const isPolicyScenario = (s: unknown): s is PolicyScenario => typeof s === 'string' && (POLICY_SCENARIOS as readonly string[]).includes(s)
export const isPolicyStatus = (s: unknown): s is PolicyStatus => typeof s === 'string' && (POLICY_STATUSES as readonly string[]).includes(s)

interface SeedJson { version: number; verifiedAt: string; note: string; providers: { id: string; label: string; scenarios: Record<string, { status: string; note?: string; sourceUrl?: string }> }[] }
const SEED = seed as SeedJson

/** The lead-maintained JSON seed as a table (every cell carries the seed's verification date). */
export function seedTable(): PolicyTable {
  return {
    version: SEED.version,
    verifiedAt: SEED.verifiedAt,
    note: SEED.note,
    providers: SEED.providers.map((p) => ({
      id: p.id,
      label: p.label,
      scenarios: Object.fromEntries(
        Object.entries(p.scenarios)
          .filter(([scenario, cell]) => isPolicyScenario(scenario) && isPolicyStatus(cell.status))
          .map(([scenario, cell]) => [scenario, { status: cell.status as PolicyStatus, note: cell.note ?? null, sourceUrl: cell.sourceUrl ?? null, verifiedAt: SEED.verifiedAt }]),
      ),
    })),
  }
}

/** Seed as `provider_policy` rows (what `scripts/seed-provider-policy.ts` upserts). */
export function seedRows(): PolicyRow[] {
  return seedTable().providers.flatMap((p) => POLICY_SCENARIOS.flatMap((scenario) => {
    const cell = p.scenarios[scenario]
    return cell ? [{ provider: p.id, scenario, status: cell.status, note: cell.note, sourceUrl: cell.sourceUrl, verifiedAt: cell.verifiedAt, overridden: false }] : []
  }))
}

/**
 * DB rows over the seed: a row replaces the seed cell (admin override wins), labels and provider
 * order stay the seed's, providers only known to the DB are appended with their id as label.
 * Rows with an unknown scenario/status are ignored. No rows → the seed unchanged.
 */
export function tableFromRows(rows: readonly PolicyRow[], base: PolicyTable = seedTable()): PolicyTable {
  const providers = base.providers.map((p) => ({ ...p, scenarios: { ...p.scenarios } }))
  for (const row of rows) {
    if (!isPolicyScenario(row.scenario) || !isPolicyStatus(row.status)) continue
    let provider = providers.find((p) => p.id === row.provider)
    if (!provider) providers.push((provider = { id: row.provider, label: row.provider, scenarios: {} }))
    provider.scenarios[row.scenario] = { status: row.status, note: row.note, sourceUrl: row.sourceUrl, verifiedAt: row.verifiedAt, ...(row.overridden ? { overridden: true } : {}) }
  }
  const dates = providers.flatMap((p) => Object.values(p.scenarios).map((c) => c?.verifiedAt ?? null)).filter((d): d is string => Boolean(d)).sort()
  return { ...base, providers, verifiedAt: dates.at(-1) ?? base.verifiedAt }
}

/** The cell for one provider in one scenario, or null when the table does not cover it. */
export function policyFor(providerId: string | null | undefined, scenario: PolicyScenario, table: PolicyTable = seedTable()): PolicyCell | null {
  if (!providerId) return null
  return table.providers.find((p) => p.id === providerId)?.scenarios[scenario] ?? null
}

/**
 * Which scenario a run/workflow is in: `local` (owner's computer) → a · cloud + own API key → c ·
 * cloud + Bystep credits → d. Accepts either the executor or a machine id, and the frozen payer
 * (`credential_source`, or the new `paid_by`).
 */
export function scenarioFor(x: { executor?: string | null; machineId?: string | null; execLocation?: string | null; credentialSource?: string | null; paidBy?: string | null }): PolicyScenario {
  if (x.executor === 'local' || x.execLocation === 'local' || x.machineId || x.credentialSource === 'local' || x.paidBy === 'user_subscription') return 'a'
  if (x.paidBy === 'byok' || x.credentialSource === 'byoa' || x.credentialSource === 'api_key' || x.credentialSource === 'connection' || x.credentialSource === 'legacy') return 'c'
  return 'd'
}

const AGENT_PROVIDER: Record<string, string> = { claude: 'anthropic', codex: 'openai', gemini: 'google', cursor: 'cursor', copilot: 'github' }
const CONNECTION_PROVIDER: Record<string, string> = { anthropic: 'anthropic', openai: 'openai', google: 'google', gemini: 'google', openrouter: 'byok', custom: 'byok' }

/**
 * Which policy row governs an option. On the owner's computer (a) it is the agent's vendor (a
 * project script / unknown agent → null: no badge). With an API key (c) it is the key's vendor.
 * On Bystep's own key (d) the agent's vendor, otherwise the Bystep gateway row (`byok`).
 */
export function providerFor(x: { scenario: PolicyScenario; agent?: string | null; connectionProvider?: string | null }): string | null {
  const agent = x.agent ? (x.agent.startsWith('custom') ? null : x.agent) : null
  if (x.scenario === 'a') return agent ? AGENT_PROVIDER[agent] ?? (agent === 'openclaw' || agent === 'hermes' || agent === 'opencode' ? 'byok' : null) : null
  if (x.scenario === 'c') return (x.connectionProvider ? CONNECTION_PROVIDER[x.connectionProvider] : null) ?? (agent ? AGENT_PROVIDER[agent] ?? 'byok' : 'byok')
  return agent ? AGENT_PROVIDER[agent] ?? 'byok' : 'byok'
}

/** `forbidden` options are hidden rather than offered and refused. */
export const policyHides = (cell: PolicyCell | null | undefined) => cell?.status === 'forbidden'
/** `gray` options stay available but carry a dated badge + tooltip. */
export const policyWarns = (cell: PolicyCell | null | undefined) => cell?.status === 'gray'

// ---------------------------------------------------------------- honest labels (E3 §4, lead amendment #4)

export type ExecLocation = 'cloud' | 'local'
export type PaidBy = 'platform' | 'byok' | 'user_subscription'
export const isExecLocation = (s: unknown): s is ExecLocation => s === 'cloud' || s === 'local'
export const isPaidBy = (s: unknown): s is PaidBy => s === 'platform' || s === 'byok' || s === 'user_subscription'

/** What a NEW run/workflow stores at enqueue: `exec_location = executor`, `paid_by` from the frozen payer. */
export function execLabelsFor(x: { executor: string | null | undefined; credentialSource: string | null | undefined }): { execLocation: ExecLocation; paidBy: PaidBy } {
  if (x.executor === 'local' || x.credentialSource === 'local') return { execLocation: 'local', paidBy: 'user_subscription' }
  return { execLocation: 'cloud', paidBy: x.credentialSource === 'platform' ? 'platform' : 'byok' }
}

/**
 * The label a row shows: its own columns when set (new rows), otherwise derived from
 * `credential_source` (+ machine) — old rows stay NULL, nothing is backfilled.
 */
export function honestLabels(row: { execLocation?: string | null; paidBy?: string | null; credentialSource?: string | null; machineId?: string | null }): { execLocation: ExecLocation; paidBy: PaidBy; derived: boolean } {
  if (isExecLocation(row.execLocation) && isPaidBy(row.paidBy)) return { execLocation: row.execLocation, paidBy: row.paidBy, derived: false }
  return { ...execLabelsFor({ executor: row.machineId || row.credentialSource === 'local' ? 'local' : 'cloud', credentialSource: row.credentialSource ?? 'platform' }), derived: true }
}

/** `paid_by` → the E1 payer vocabulary of `session-card` (`bystep` · `api_key` · `subscription`). */
export const payerOfPaidBy = (paidBy: PaidBy) => (paidBy === 'platform' ? 'bystep' : paidBy === 'byok' ? 'api_key' : 'subscription')
